// 1:1 with api
export type Evaluation = {
  id: string;
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
  name: string;
  description?: string;
  metricProviders: {
    metricProviderId: string;
    versionId?: string;
  }[];
  isDeprecated: boolean;
};

export type MetricProvider = {
  id: string;
  name: string;
  description?: string;
  versions: MetricProviderVersion[];
  createdAt: string;
};

export type MetricProviderVersion = {
  id: string;
  version: number;
};

export type MetricConfigAPIHeader = {
  header: string;
  value: string;
};

export type MetricConfigAPI = {
  url: string;
  headers: MetricConfigAPIHeader[]
  isAsync?: boolean;
};

export type MetricConfigLLMScore = {
  prompt: string;
  modelParameters: {
    model: string;
    temperature: string;
  };
  promptParameters?: Record<string, string>;
};

export type MetricConfig = {
  id: string;
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
  name: string;
  metricProviderId: string;
  versionId: string;
  version?: number;
  exampleType: ExampleTypeEnum[];
  configuration: MetricConfigAPI | MetricConfigLLMScore;
  producesMetrics: string[];
  isDeprecated: boolean;
  description?: string;
  latestVersionNumber: number;
};

export type Run = {
  id: string;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  evaluationId: string;
  sourceId?: string;
  sourceVersion?: number;
  sourceType: SourceTypeEnum;
  scheduleId?: string;
  status: string;
  statusReason?: string;
  totalExamples?: number;
  totalResults?: number;
  startedAt?: string;
  completedAt?: string;
};

export type Result = {
  id: string;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  runId: string;
  exampleId: string;
  exampleType: string;
  metricProviderId: string;
  metricProviderVersionId: string;
  metricName: string;
  score: string;
  executionTime: number;
  scoreMetaData?: {
    reason?: string;
  };
  resultStatus: string;
  resultStatusReason?: string;
};

export type SourceConfigSnowflake = {
  credential_ref: string;
  database_name: string;
  query: string;
  schema_name?: string;
};

export type SourceConfigJson = Record<string, unknown>;

export type SourceConfigAgentLogs = {
  filters: {
    agent_name?: string | null;
    agent_id?: string | null;
    component_name?: string | null;
    constellation_name?: string | null;
    constellation_version?: string | null;
    client_id?: string | null;
    correlation_id?: string | null;
    request_id?: string | null;
    metadata?: Record<string, unknown> | null;
    from_start_time?: string | null;
    to_end_time?: string | null;
  };
  row_limit?: number | null;
};

export type SourceConfigModelInvocationLogs = {
  filters: {
    agent_name?: string | null;
    agent_id?: string | null;
    constellation_name?: string | null;
    constellation_version?: string | null;
    client_id?: string | null;
    correlation_id?: string | null;
    request_id?: string | null;
    requester_tags?: string[] | null;
    requester_metadata?: Record<string, unknown> | null;
    from_start_time?: string | null;
    to_end_time?: string | null;
  };
  row_limit?: number | null;
  context_window?: number | null;
};

export type SourceConfigCompletionLogs = {
  filters: {
    agent_name?: string | null;
    agent_id?: string | null;
    constellation_name?: string | null;
    constellation_version?: string | null;
    client_id?: string | null;
    correlation_id?: string | null;
    request_id?: string | null;
    from_start_time?: string | null;
    to_end_time?: string | null;
  };
  row_limit?: number | null;
};

export type SourceConfig = SourceConfigSnowflake | SourceConfigJson | SourceConfigAgentLogs | SourceConfigModelInvocationLogs | SourceConfigCompletionLogs;

type SourceBase = {
  id: string;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  name: string;
  description?: string;
  status: 'active' | 'error';
  example_type: 'message' | 'conversation';
  example_count?: number;
  last_sync?: string;
  is_deprecated: boolean;
  version: number;
  version_created_at: string;
  version_updated_at?: string | null;
  version_created_by?: string | null;
  latest_version_number: number;
};

export type SourceJson = SourceBase & { source_type: 'json', config: SourceConfigJson };
export type SourceSnowflake = SourceBase & { source_type: 'snowflake', config: SourceConfigSnowflake };
export type SourceAgentLogs = SourceBase & { source_type: 'mas_agent_action_log', config: SourceConfigAgentLogs };
export type SourceModelInvocationLogs = SourceBase & { source_type: 'mas_model_invocation_log', config: SourceConfigModelInvocationLogs };
export type SourceCompletionLogs = SourceBase & { source_type: 'mas_completion_log', config: SourceConfigCompletionLogs };

export type Source = SourceJson | SourceSnowflake | SourceAgentLogs | SourceModelInvocationLogs | SourceCompletionLogs;

export type SourcePreview = {
  sourceId: string;
  items: Example[];
  totalCount: number;
  previewCount: number;
};
export type Details<T extends object = object> = {
  details?: T
};

export type DetailRunInfo = {
  lastRunDate?: string;
  lastRunStatus?: string;
  runCount: number;
};

export type DetailMetricInfo = {
  metricProviderId: string;
  metricName: string;
  numResults: number;
  numNumericResults: number;
  averageScore?: number;
  completionStatus: string;
};

export type DetailMetricsInfo = {
  metrics: DetailMetricInfo[];
};

export type EvaluationWithDetails<T extends object = object> = {
  evaluation: Evaluation;
} & Details<T>;

export type RunWithDetails<T extends object = object> = {
  run: Run;
} & Details<T>;

export const METRIC_TYPES = {
  API_CLASSIFIER: 'API Classifier',
  LLM_AS_A_JUDGE: 'LLM as a Judge'
} as const;
export type MetricTypeEnum = typeof METRIC_TYPES[ keyof typeof METRIC_TYPES ];

export const SOURCE_TYPES = {
  JSON: 'json',
  SNOWFLAKE: 'snowflake',
  AGENT_ACTION_LOGS: 'mas_agent_action_log',
  MODEL_INVOCATION_LOGS: 'mas_model_invocation_log',
  COMPLETION_LOGS: 'mas_completion_log'
} as const;
export type SourceTypeEnum = typeof SOURCE_TYPES[ keyof typeof SOURCE_TYPES ];

export const SOURCE_TYPE_DISPLAY_NAMES: Record<SourceTypeEnum, string> = {
  [ SOURCE_TYPES.JSON ]: 'JSON',
  [ SOURCE_TYPES.SNOWFLAKE ]: 'Snowflake',
  [ SOURCE_TYPES.AGENT_ACTION_LOGS ]: 'Agent Action Logs',
  [ SOURCE_TYPES.MODEL_INVOCATION_LOGS ]: 'Model Invocation Logs',
  [ SOURCE_TYPES.COMPLETION_LOGS ]: 'Request/Response Logs'
};

// Helper subtitles shown beneath each option label in the Source Type dropdown.
export const SOURCE_TYPE_HELPER: Record<SourceTypeEnum, string> = {
  [ SOURCE_TYPES.JSON ]: 'JSON examples',
  [ SOURCE_TYPES.SNOWFLAKE ]: 'Database examples',
  [ SOURCE_TYPES.AGENT_ACTION_LOGS ]: 'SDK-emitted agent events',
  [ SOURCE_TYPES.MODEL_INVOCATION_LOGS ]: 'Model requests',
  [ SOURCE_TYPES.COMPLETION_LOGS ]: 'Request lifecycle'
};

export const EXAMPLE_TYPES = {
  CONVERSATION: 'conversation',
  MESSAGE: 'message'
} as const;
export type ExampleTypeEnum = typeof EXAMPLE_TYPES[ keyof typeof EXAMPLE_TYPES ];

export const EXAMPLE_TYPE_DISPLAY_NAMES: Record<ExampleTypeEnum, string> = {
  [ EXAMPLE_TYPES.CONVERSATION ]: 'Conversation',
  [ EXAMPLE_TYPES.MESSAGE ]: 'Message'
};

/**
 * A single turn in an Example. Mirrors aidp-eval's `Message` discriminated
 * union: a turn carries either textual content (`message`) or a structured
 * payload (`data`), never both. The create/run zod schemas validate the
 * exactly-one constraint at runtime and produce values typed as this union.
 */
export type TextMessage = {
  user: string;
  message: string;
};

export type StructuredMessage = {
  user: string;
  data: unknown;
};

export type ExampleMessage = TextMessage | StructuredMessage;

/** Narrows an ExampleMessage to its structured variant. */
export const isStructuredMessage = ( msg: ExampleMessage ): msg is StructuredMessage => 'data' in msg;

export type Example = {
  id: string;
  type: ExampleTypeEnum;
  data: ExampleMessage[];
  meta_data?: Record<string, unknown> | null;
  tags?: Record<string, string | number | boolean> | null;
};



/* ===== API TYPES ===== */
export type EvalCreateReq = {
  name: string;
  description?: string;
  metricProviders: {
    metricProviderId: string;
    version?: string;
  }[]
};

export type EvalCreateRes = {
  evaluation: Evaluation;
};

export type EvalRunsGetRes = {
  evaluation: EvaluationWithDetails<DetailRunInfo>;
  runs: RunWithDetails<DetailMetricsInfo>[];
};

export type EvalRunsResultsGetRes = {
  evaluation: EvaluationWithDetails<DetailRunInfo>;
  runs: RunWithDetails<DetailMetricsInfo>[];
  results: {
    [ key: string ]: Result[];
  };
};

export type EvalsGetRes = EvaluationWithDetails<DetailRunInfo>[];

export type EvalsSearchParams = {
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  name?: string;
  is_deprecated?: boolean;
  created_at_from?: string;
  created_at_to?: string;
  include_details?: boolean;
};

export type EvalsSearchRes = {
  items: EvaluationWithDetails<DetailRunInfo>[];
  pagination: {
    nextCursor: string | null;
    hasMore: boolean;
    limit: number;
  };
};

export type EvaluationDeprecateReq = {
  isDeprecated: boolean;
};

export type MetricCreateReq = {
  name: string;
  description: string;
  exampleType: ( 'message' | 'conversation' )[];
  configuration: MetricConfigAPI | MetricConfigLLMScore;
  producesMetrics: string[];
};

export type MetricCreateRes = MetricProvider;

export type MetricConfigGetRes = MetricConfig;

export type MetricsGetRes = MetricProvider[];

export type MetricsSearchParams = {
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  name?: string;
  created_at_from?: string;
  created_at_to?: string;
  is_deprecated?: boolean;
};

export type MetricsSearchRes = {
  items: MetricConfig[];
  pagination: {
    nextCursor: string | null;
    hasMore: boolean;
    limit: number;
  };
};

export type MetricGetRes = MetricProvider;

export type MetricVersionDeprecateReq = {
  isDeprecated: boolean;
};

export type MetricVersionCreateReq = {
  exampleType: ExampleTypeEnum[];
  configuration: MetricConfigAPI | MetricConfigLLMScore;
  producesMetrics: string[];
};

/**
 * Clone endpoint accepts a Partial of MetricCreateReq — any field omitted is
 * inherited from the source version. Naming uses exactly one of name,
 * nameSuffix, or namePrefix (backend enforces this).
 */
export type MetricCloneReq = {
  name?: string;
  nameSuffix?: string;
  namePrefix?: string;
  description?: string;
  exampleType?: ExampleTypeEnum[];
  configuration?: MetricConfigAPI | MetricConfigLLMScore;
  producesMetrics?: string[];
};

export type RunCreateReq = {
  dataSource: {
    dataOrigin: 'reference';
    sourceId: string;
    sourceVersion: number | 'latest';
  } | {
    dataOrigin: 'inline';
    examples: Array<{
      id?: string;
      type: 'message' | 'conversation';
      data: Array<{
        user: 'user' | 'assistant';
        message: string;
      }>;
      metaData?: Record<string, unknown>;
    }>;
  };
  includeReason: boolean;
};

export type RunCreateRes = Run;

export type RunGetRes = RunWithDetails<DetailMetricsInfo>;

export type RunsGetReq = {
  runIds: string[];
};

export type ScheduleState = 'ACTIVE' | 'PAUSED' | 'DISABLED';

export type ScheduleCreateReq = {
  evaluation_id: string;
  source_id: string;
  source_version?: number;
  name?: string;
  description?: string;
  cron_expression: string;
  timezone?: string;
};

export type SchedulesListParams = {
  evaluation_id?: string;
  source_id?: string;
  state?: ScheduleState;
  limit?: number;
  offset?: number;
  include_details?: boolean;
};

export type SourceCreateReq<T = unknown> = {
  name: string;
  description?: string;
  source_type: SourceTypeEnum;
  config: T;
  example_type: ExampleTypeEnum;
};

export type SourceUpdateReq = {
  config: SourceConfig;
};

export type SourceUpdateRes = Source;

export type SourceCreateRes = Source;

export type SourceGetRes = Source;

export type SourcePreviewGetRes = SourcePreview;

export type SourcesGetRes = Source[];

export type SourcesSearchParams = {
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  name?: string;
  source_type?: string;
  example_type?: string;
  created_at_from?: string;
  created_at_to?: string;
  is_deprecated?: boolean;
};

export type SourcesSearchRes = {
  items: Source[];
  pagination: {
    next_cursor: string | null;
    has_more: boolean;
    limit: number;
  };
};

export type SourceValidateRes = {
  status: 'success' | 'error';
  message: string;
  totalExamples?: number;
};

export type ScheduleDisabledReason =
  | { type: 'evaluation_deprecated'; evaluation_id: string }
  | { type: 'metric_provider_version_deprecated'; metric_provider_version_id: string }
  | { type: 'metric_invalid_llm_model'; metric_provider_version_id: string; llm_model: string; detail: string }
  | { type: 'source_unavailable'; source_id: string; detail: string }
  | { type: 'source_version_unavailable'; source_id: string; source_version: number }
  | { type: 'source_deprecated'; source_id: string; source_version: number };

export type ScheduleResponse = {
  id: string;
  created_at: string;
  updated_at?: string | null;
  created_by?: string | null;
  evaluation_id: string;
  source_id: string;
  source_version: number | null;
  name: string | null;
  description: string | null;
  cron_expression: string;
  timezone: string;
  state: 'ACTIVE' | 'PAUSED' | 'DISABLED';
  next_fire_at: string;
  disabled_reason: ScheduleDisabledReason | null;
};

export type ScheduleWithDetails = {
  schedule: ScheduleResponse;
  details: {
    run_count: number;
    last_run_id: string | null;
    last_run_status: string | null;
    last_fire_at: string | null;
  } | null;
};

export type SchedulesListRes = ScheduleWithDetails[];

export type SchedulesSearchParams = {
  limit?: number;
  cursor?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  name?: string;
  description?: string;
  evaluation_id?: string;
  source_id?: string;
  state?: ScheduleState;
  created_at_from?: string;
  created_at_to?: string;
};

export type SchedulesSearchRes = {
  items: ScheduleWithDetails[];
  pagination: {
    next_cursor: string | null;
    has_more: boolean;
    limit: number;
  };
};

export type BulkScheduleSelector = {
  evaluation_id?: string;
  source_id?: string;
  source_version?: number;
};

export type DeleteScheduleResult = {
  fga_cleanup_failed_ids: string[];
};

export type BulkScheduleResult = {
  affected_schedule_ids: string[];
  fga_cleanup_failed_ids: string[];
};

// SSE streaming types for run progress
export const TERMINAL_RUN_STATUSES = [ 'completed', 'failed', 'cancelled', 'incomplete' ] as const;

export type RunProgressMessage = {
  runId: string;
  runStatus: string;
  totalSyncTasks: number;
  totalAsyncTasks: number;
  syncRunning: number;
  syncSucceeded: number;
  syncFailed: number;
  asyncSubmitting: number;
  asyncSubmitSucceeded: number;
  asyncSubmitFailed: number;
  syncPercentComplete: number;
};

export type RunCompletedMessage = {
  runId: string;
  runStatus: string;
};

export type RunStreamError = {
  message: string;
  completed: true;
};

export type SourceVersionDeprecateReq = {
  is_deprecated: boolean;
};


/* ===== UI TYPES ===== */
export type ExampleMetricDataMap = {
  [ exampleId: string ]: {
    [ MetricProviderIdPipeMetricName: string ]: Result;
  }
};

export type RunMetricDataMap = {
  [ runId: string ]: MetricDataMap;
};

export type MetricDataMap = {
  [ MetricProviderIdPipeMetricName: string ]: DetailMetricInfo;
};

export type MetricVersionConfigDataMap = {
  [ MetricProviderIdPipeVersion: string ]: MetricConfig;
};
